import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaBell, FaSpinner, FaBellSlash, FaClock, FaTrash, FaTasks, FaClipboardList, FaProjectDiagram, FaExclamationCircle, FaInfoCircle } from 'react-icons/fa';
import { apiGet, apiDelete, apiPut } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext.jsx';

const NotificationModal = ({
  isOpen,
  onClose
}) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Charger les notifications
  const loadNotifications = async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet('/api/notifications');
      if (response.success) {
        setNotifications(response.data || []);
        setUnreadCount(response.unreadCount || 0);
      } else {
        setError(response.message || 'Erreur lors du chargement des notifications.');
      }
    } catch (err) {
      setError('Erreur réseau ou serveur.');
      console.error('Erreur lors du chargement des notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Charger les notifications quand la modale s'ouvre
  useEffect(() => {
    if (isOpen && user) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000); // 30 secondes
      return () => clearInterval(interval);
    }
  }, [isOpen, user]);

  // Marquer une notification comme lue
  const markAsRead = async (notificationId) => {
    try {
      const response = await apiPut(`/notifications/${notificationId}/read`);
      if (response.success) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId ? { ...notif, statut: 'lu' } : notif
          )
        );
        setUnreadCount(response.unreadCount || 0);
      }
    } catch (err) {
      console.error('Erreur lors du marquage de la notification:', err);
    }
  };

  // Marquer toutes les notifications comme lues
  const markAllAsRead = async () => {
    try {
      const response = await apiPut('/api/notifications/mark-all-read');
      if (response.success) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, statut: 'lu' }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Erreur lors du marquage de toutes les notifications:', err);
    }
  };

  // Supprimer une notification
  const deleteNotification = async (notificationId, event) => {
    if (event) event.stopPropagation();
    try {
      const response = await apiDelete(`/api/notifications/${notificationId}`);
      if (response.success) {
        setNotifications(prev => 
          prev.filter(notif => notif.id !== notificationId)
        );
        setUnreadCount(response.unreadCount || 0);
      }
    } catch (err) {
      console.error('Erreur lors de la suppression de la notification:', err);
    }
  };

  // Supprimer toutes les notifications
  const clearAllNotifications = async () => {
    try {
      for (const notification of notifications) {
        await apiDelete(`/api/notifications/${notification.id}`);
      }
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Erreur lors de la suppression de toutes les notifications:', err);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Il y a ${Math.floor(diffInMinutes / 60)}h`;
    return date.toLocaleDateString('fr-FR');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task_assigned':
      case 'task_created':
        return <FaTasks className="text-blue-500" />;
      case 'task_status_changed':
        return <FaClipboardList className="text-green-500" />;
      case 'task_due_soon':
      case 'task_overdue':
        return <FaExclamationCircle className="text-red-500" />;
      case 'project_assigned':
      case 'project_status_changed':
        return <FaProjectDiagram className="text-purple-500" />;
      case 'system_alert':
        return <FaInfoCircle className="text-yellow-500" />;
      default:
        return <FaBell className="text-gray-500" />;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[10000] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white rounded-2xl shadow-2xl p-3 w-full max-w-sm max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FaSpinner className="animate-spin text-blue-500 text-2xl mb-4" />
              <p className="text-sm text-gray-500">Chargement des notifications...</p>
            </div>
          ) : error ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
              <p>❌ {error}</p>
              <button 
                onClick={loadNotifications} 
                className="mt-2 px-4 py-2 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 transition-colors"
              >
                Réessayer
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FaBellSlash className="text-gray-300 text-5xl mb-4" />
              <p className="text-sm text-gray-500">Aucune notification pour le moment</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    notification.statut === 'non_lu' 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => notification.statut === 'non_lu' && markAsRead(notification.id)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm text-gray-900">
                          {notification.titre}
                        </p>
                        {notification.statut === 'non_lu' && (
                          <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1"></div>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                        <FaClock className="w-3 h-3" />
                        <span>{formatTime(notification.created_at || notification.date_creation)}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteNotification(notification.id, e)}
                      className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-gray-600 hover:text-gray-900 transition-colors"
              >
                Marquer tout comme lu
              </button>
            )}
            <button
              onClick={clearAllNotifications}
              className="text-xs text-red-600 hover:text-red-700 transition-colors flex items-center gap-1"
            >
              <FaTrash className="w-3 h-3" />
              Tout supprimer
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default NotificationModal;






